import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, Edit, People } from '@mui/icons-material';
import { FC, useState } from 'react';

import { useAuth } from '@carcareers/hooks';
import { useJobseekerDeleteReferenceMutation, useJobseekerReferencesQuery } from '@carcareers/store';
import { JobseekerReferenceModal } from '@carcareers/components';
import { JobseekerReference } from '@libs/models';

export const ReferencesCard: FC = () => {
  const { session } = useAuth();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editReference, setEditReference] = useState<JobseekerReference | null>(null);

  const [deleteReference, { isLoading: isDeleting }] = useJobseekerDeleteReferenceMutation();
  const { data: references } = useJobseekerReferencesQuery(session?.profile?.id ?? 0, {
    skip: !session?.profile?.id,
  });

  const handleOpenAdd = () => setIsAddOpen(true);
  const handleCloseAdd = () => setIsAddOpen(false);

  const handleEditReference = (reference: JobseekerReference) => {
    setEditReference(reference);
  };

  const handleCloseEdit = () => {
    setEditReference(null);
  };

  const handleDeleteReference = (referenceId: number) => {
    const jobseekerId = session?.profile?.id ?? 0;
    deleteReference({ jobseekerId, referenceId });
  };

  return (
    <>
      <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: '16px !important' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
              <People sx={{ mr: 1 }} />
              References
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              size="small"
              sx={{
                borderRadius: 2,
                height: 32,
                mt: { xs: 2, md: 0 },
                display: { xs: 'none', md: 'flex' },
              }}
              onClick={handleOpenAdd}
            >
              Add Reference
            </Button>
          </Box>

          <List
            sx={{
              '& .MuiListItemSecondaryAction-root': {
                position: 'unset',
                top: '32px',
                transform: 'unset',
              },
            }}
          >
            {references?.map((reference, index) => (
              <Box key={reference.id}>
                <ListItem
                  sx={{ px: 0 }}
                  alignItems="flex-start"
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        color="secondary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleEditReference(reference)}
                      >
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="secondary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleDeleteReference(reference.id)}
                      >
                        {isDeleting ? <CircularProgress size={18} color="secondary" /> : <Delete sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={reference.name}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="primary">
                          {reference.position} ({reference.relationship})
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {reference.email} | {reference.phone}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < (references?.length ?? 0) - 1 && <Divider />}
              </Box>
            ))}
          </List>

          <Button
            variant="outlined"
            startIcon={<Add />}
            size="small"
            sx={{
              borderRadius: 2,
              height: 32,
              width: '100%',
              mt: 2,
              display: { xs: 'flex', md: 'none' },
            }}
            onClick={handleOpenAdd}
          >
            Add Reference
          </Button>
        </CardContent>
      </Card>

      <JobseekerReferenceModal type="add" open={isAddOpen} onClose={handleCloseAdd} />

      {editReference && (
        <JobseekerReferenceModal
          type="edit"
          open={!!editReference}
          onClose={handleCloseEdit}
          reference={editReference}
        />
      )}
    </>
  );
}; 